import { Button, ButtonGroup, styled, SxProps, Typography } from '@mui/material';
import { theme } from '@src/theme';
import { buildDataTestId, buildDataTestIdAttribute, buildDataTestIdFromNestedName, replaceCebabCase } from '@src/utils';
import { FC } from 'react';

const border = '1px solid #C7CDD6';
const hoverBorder = '1px solid #999FAB';

const SegmentButton = styled(Button)<{ active: number }>(({ active }) => ({
  minWidth: 0,
  boxSizing: 'border-box',
  backgroundColor: '#FFF',
  padding: '12px 18px',
  border: active ? `2px solid ${theme.palette.secondary.main}` : border,
  span: {
    fontWeight: 600,
    color: active ? theme.palette.secondary.main : theme.palette.grey['700'],
  },
  color: active ? theme.palette.secondary.main : theme.palette.grey['700'],
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '18px',
  position: 'relative',
  flexShrink: 0,
  '&.Mui-disabled': {
    border: `2px solid ${theme.palette.grey['300']}`,
    backgroundColor: active ? theme.palette.grey['300'] : theme.palette.grey['200'],
    fontWeight: 500,
    color: theme.palette.grey['400'],
  },
  '&.MuiButtonGroup-firstButton': {
    borderRadius: '2px 0px 0px 2px',
    '&:not(.Mui-disabled)': {
      borderRight: active ? `2px solid ${theme.palette.secondary.main}` : border,
    },
    '&:hover + button[active="0"]': active
      ? null
      : {
          borderLeft: hoverBorder,
        },
    '& + button': active
      ? {
          borderLeft: 0,
          marginLeft: 0,
        }
      : null,
  },
  '&.MuiButtonGroup-middleButton': {
    '&:not(.Mui-disabled)': {
      borderRight: active ? `2px solid ${theme.palette.secondary.main}` : border,
      '&:hover + button[active="0"]': active
        ? null
        : {
            borderLeft: hoverBorder,
          },
      '& + button': active
        ? {
            borderLeft: 0,
            marginLeft: 0,
          }
        : null,
    },
  },
  '&.MuiButtonGroup-lastButton': {
    borderRadius: '0px 2px 2px 0px',
  },
  '&:hover': {
    border: active ? `2px solid ${theme.palette.secondary.light}` : hoverBorder,
    color: theme.palette.secondary.main,
    backgroundColor: '#FFF',
  },
}));

type SegmentedControlProps = {
  values: { name: string; value: string }[];
  active: string;
  handleClick: (val: any) => void;
  disabled?: boolean;
  sx?: SxProps;
  withTypography?: boolean;
  fontWeight?: number;
  prefix?: string;
  dataTestId?: string;
};
export const SegmentedControl: FC<SegmentedControlProps> = ({
  values,
  active,
  handleClick,
  sx,
  withTypography = false,
  disabled = false,
  fontWeight,
  prefix,
  dataTestId,
}) => {
  return (
    <ButtonGroup variant="outlined" sx={{ flexShrink: 0, ...sx }} color="secondary" disableRipple disabled={disabled}>
      {values.map(({ name, value }) => {
        const dataTestIdButton = dataTestId
          ? buildDataTestId(dataTestId, replaceCebabCase(value))
          : buildDataTestIdFromNestedName(prefix, 'button', value);
        return (
          <SegmentButton
            active={+(value === active)}
            disabled={disabled}
            onClick={() => handleClick(value)}
            key={value}
            sx={{ fontWeight }}
            {...buildDataTestIdAttribute(dataTestIdButton)}
          >
            {withTypography ? (
              <Typography variant="label12Bold" lineHeight="16px">
                {name}
              </Typography>
            ) : (
              name
            )}
          </SegmentButton>
        );
      })}
    </ButtonGroup>
  );
};
